import {compact} from '@benchling/js-utils';

import {InterfaceType, DomainGraphInterface} from 'app-core/framework/CoreObjectConstants';
import {
  getCapabilitiesForModelType,
  getObjectTypeForModelTypeConstant,
} from 'app-core/framework/CoreObjectIntrospectionUtils';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants, {SequenceSubtype} from 'constants/ModelTypeConstants';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {areAaPartFeaturesEnabledForAntibody} from 'molbio/aa-sequences/util/aminoAcidPartsUtil';
import {REACTION_PSEUDO_TYPE} from 'search/ReactionSearchConstants';
import {isStudyableObjectType} from 'studies/studyable/StudyableUtils';

import * as registeredSearchPlugins from './plugins/registeredSearchPlugins';
import {FilterKey, QuickFilterKey} from './SearchConstants';

const BIOENTITY_FILTER_KEYS: Array<FilterKey> = compact([
  FilterKey.ARCHIVE_PURPOSES,
  FilterKey.ANCESTOR_FOLDER,
  FilterKey.AUTHOR,
  FilterKey.BIOENTITY_LABELS,
  FilterKey.CREATED_AT,
  FilterKey.CREATOR,
  FilterKey.CUSTOM_TAG,
  FilterKey.FOLDER,
  FilterKey.FOLDER_SECTION,
  FilterKey.MODIFIED_AT,
  FilterKey.NAMES,
  FilterKey.NOTE_REFERENCE,
  FilterKey.PROJECT_FOLDER,
  FilterKey.REGISTRY_COLLECTION_OWNER,
  FilterKey.SAMPLE_DEFINITION,
  FilterKey.SCHEMA,
  FilterKey.SCHEMA_INTERFACE,
  FilterKey.SCHEMA_FIELD,
  FilterKey.SCHEMA_VALIDATION_ERROR_LABEL,
  FilterKey.SCHEMA_VALIDATION_STATUS,
  FilterKey.REGISTRATION_STATUS,
  BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS ? FilterKey.ITEM_IN_WORKLIST : null,
  FilterKey.IN_STUDY,
]);

const STORABLE_FILTER_KEYS: Array<FilterKey> = [
  FilterKey.ARCHIVE_PURPOSES,
  FilterKey.ANCESTOR_FOLDER,
  FilterKey.ANCESTOR_STORABLE,
  FilterKey.BARCODES,
  FilterKey.CONTAINER_CONTENTS,
  FilterKey.CONTAINER_CONTENT_SCHEMA,
  FilterKey.CONTAINER_PARENT,
  FilterKey.CREATED_AT,
  FilterKey.CREATOR,
  FilterKey.MODIFIED_AT,
  FilterKey.NAMES,
  FilterKey.PARENT_STORABLE,
  FilterKey.REGISTRY,
  FilterKey.SCHEMA,
  FilterKey.SCHEMA_FIELD,
  FilterKey.STARRED,
];

const ELN_FILTER_KEYS: Array<FilterKey> = [
  FilterKey.ARCHIVE_PURPOSES,
  FilterKey.ANCESTOR_FOLDER,
  FilterKey.AUTHOR,
  FilterKey.CREATED_AT,
  FilterKey.MODIFIED_AT,
  FilterKey.NAMES,
  FilterKey.NOTE_REFERENCE,
];

const PROCESS_FILTER_KEYS: Array<FilterKey> = [
  FilterKey.ARCHIVE_PURPOSES,
  FilterKey.ANCESTOR_FOLDER,
  FilterKey.AUTHOR,
  FilterKey.CREATED_AT,
  FilterKey.MODIFIED_AT,
  FilterKey.NAMES,
  FilterKey.CREATOR,
  FilterKey.FOLDER,
  FilterKey.FOLDER_SECTION,
  FilterKey.PROCESS_STATUS,
];

const PROJECT_FILTER_KEYS: Array<FilterKey> = [
  FilterKey.ARCHIVE_PURPOSES,
  FilterKey.SHARED_WITH,
  FilterKey.PROJECT_ATTRIBUTE,
  FilterKey.HIDDEN,
  FilterKey.OWNER,
  FilterKey.OWNER_OR_COLLABORATOR,
  FilterKey.REVIEW_LIFECYCLE,
  FilterKey.REVIEW_LIFECYCLE_TYPE,
  FilterKey.STARRED,
  FilterKey.TOP_LEVEL_PERMISSIONS,
];

const REVIEWABLE_FILTER_KEYS: Array<FilterKey> = [
  FilterKey.REVIEW_STARTED,
  FilterKey.REVIEW_INITIAL_STARTED,
  FilterKey.REVIEW_COMPLETED,
  FilterKey.REVIEW,
];

const ENTRY_FILTER_KEYS: Array<FilterKey> = [
  FilterKey.CREATOR,
  FilterKey.SCHEMA,
  FilterKey.SCHEMA_FIELD,
  FilterKey.IN_STUDY,
];

const COLLECTIBLE_FILTER_KEYS: Array<FilterKey> = [
  FilterKey.ARCHIVE_PURPOSES,
  FilterKey.AUTHOR,
  FilterKey.CREATED_AT,
  FilterKey.CREATOR,
  FilterKey.MODIFIED_AT,
  FilterKey.NAMES,
  FilterKey.TEMPLATE_COLLECTION,
];

const PUBLISHABLE_FILTER_KEYS: Array<FilterKey> = [...COLLECTIBLE_FILTER_KEYS, FilterKey.PUBLISHING_STATUS];

const ALL_PROCEDURE_TASK_FILTER_KEYS: Array<FilterKey> = [
  FilterKey.CREATED_AT,
  FilterKey.CREATOR,
  FilterKey.MODIFIED_AT,
  FilterKey.NAMES,
  FilterKey.ANY_LINK_SCHEMA_FIELD,
  FilterKey.IN_STUDY,
  FilterKey.ARCHIVE_STATUS,
  FilterKey.ARCHIVE_PURPOSES,
  FilterKey.PROCESS_CLIENT_ORIGIN,
  FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES,
  FilterKey.PROCEDURE_TASK_STATUS,
];

const ALL_RECIPE_TASK_FILTER_KEYS: Array<FilterKey> = [
  ...ALL_PROCEDURE_TASK_FILTER_KEYS,
  FilterKey.RECIPE_TEMPLATE,
];
const ALL_ASSAY_TASK_FILTER_KEYS: Array<FilterKey> = [
  ...ALL_PROCEDURE_TASK_FILTER_KEYS,
  FilterKey.ASSAY_TEMPLATE,
];

// Manually-defined filters. Filters derived from PlatformCapabilities should be auto-added by getFiltersForType.
export const _FILTER_KEYS_BY_TYPE: Record<string, Array<FilterKey>> = {
  [ModelTypeConstants.ANALYSIS]: [
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
  ],
  [ModelTypeConstants.ANALYSIS_TEMPLATE]: COLLECTIBLE_FILTER_KEYS,
  [ModelTypeConstants.ANALYTICS_DASHBOARD]: [
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.STARRED,
  ],
  [ModelTypeConstants.BASIC_FOLDER_ITEM]: BIOENTITY_FILTER_KEYS,
  [ModelTypeConstants.ANTIBODY]: BIOENTITY_FILTER_KEYS,
  [ModelTypeConstants.EQUIPMENT]: BIOENTITY_FILTER_KEYS,
  [ModelTypeConstants.OLIGO_CONJUGATE]: BIOENTITY_FILTER_KEYS,
  [ModelTypeConstants.OLIGO_DUPLEX]: BIOENTITY_FILTER_KEYS,
  [ModelTypeConstants.BULK_ASSEMBLY]: compact([
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.AUTHOR,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS ? FilterKey.ITEM_IN_WORKLIST : null,
  ]),
  [ModelTypeConstants.MIXTURE]: [...BIOENTITY_FILTER_KEYS, FilterKey.INGREDIENTS],
  [ModelTypeConstants.MOLECULE]: [...BIOENTITY_FILTER_KEYS, FilterKey.CHEMICAL_STRUCTURE],
  // TODO(BNCH-145194): replace this with Reaction ModelTypeConstant enum and enumerate all applicable filters.
  // Note that there could be more filters but it becomes ambiguous whether they should apply to the Molecule or corresponding Entry.
  [REACTION_PSEUDO_TYPE]: [FilterKey.CHEMICAL_STRUCTURE],
  [ModelTypeConstants.PROTEIN]: compact([
    ...BIOENTITY_FILTER_KEYS,
    BenchlingConfig.SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH ? FilterKey.ANNOTATION : null,
    areAaPartFeaturesEnabledForAntibody() ? FilterKey.AA_PART_SEQUENCE : null,
    FilterKey.ALPHAFOLD_STRUCTURE,
    FilterKey.BLAST,
    FilterKey.PROTEIN_AMINO_ACIDS,
  ]),
  [ModelTypeConstants.DNA_OLIGO]: compact([
    ...BIOENTITY_FILTER_KEYS,
    FilterKey.BLAST,
    FilterKey.SEQUENCE_BASES,
    BenchlingConfig.SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH ? FilterKey.ANNOTATION : null,
    BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? FilterKey.OLIGO_HELM_DNA : null,
    BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? FilterKey.MONOMER : null,
  ]),
  [ModelTypeConstants.RNA_OLIGO]: compact([
    ...BIOENTITY_FILTER_KEYS,
    FilterKey.SEQUENCE_BASES,
    BenchlingConfig.SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH ? FilterKey.ANNOTATION : null,
    BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? FilterKey.OLIGO_HELM_RNA : null,
    BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? FilterKey.MONOMER : null,
    FilterKey.BLAST,
  ]),
  [ModelTypeConstants.SEQUENCE]: compact([
    ...BIOENTITY_FILTER_KEYS,
    FilterKey.BLAST,
    FilterKey.SEQUENCE_BASES,
    BenchlingConfig.SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH ? FilterKey.ANNOTATION : null,
    FilterKey.PROTEIN_AMINO_ACIDS,
    FilterKey.PART_SEQUENCE,
    FilterKey.TRANSCRIPTIONS,
  ]),
  [ModelTypeConstants.RNA_SEQUENCE]: compact([
    ...BIOENTITY_FILTER_KEYS,
    FilterKey.SEQUENCE_BASES,
    BenchlingConfig.SIGHTGLASS2_ENABLE_ANNOTATION_SEARCH ? FilterKey.ANNOTATION : null,
    FilterKey.PROTEIN_AMINO_ACIDS,
    BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? FilterKey.MONOMER : null,
    FilterKey.BLAST,
    FilterKey.RNA_PART_SEQUENCE,
  ]),
  [ModelTypeConstants.REQUEST]: [
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.ASSAY_SCHEMA,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.SCHEMA_FIELD,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.REQUEST_ASSIGNEE,
    FilterKey.REQUEST_IDS,
    FilterKey.REQUEST_REQUESTOR,
    FilterKey.REQUEST_STATUS,
    FilterKey.REQUEST_TASK_SCHEMA,
    FilterKey.SCHEMA,
  ],
  [ModelTypeConstants.REQUEST_V2_SUBMISSION]: [
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.FOLDER_SECTION,
    FilterKey.FOLDER,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.REQUEST_V2_SUBMISSION_STATUS,
    FilterKey.REQUEST_V2_DEFINITION,
    FilterKey.PROCESS_WATCHER,
  ],
  [ModelTypeConstants.ASSAY_RUN]: [
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.SCHEMA,
    FilterKey.SCHEMA_FIELD,
    FilterKey.MODIFIED_AT,
    FilterKey.RUN_AVAILABILITY,
  ],
  [ModelTypeConstants.FOLDER]: [
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.BIOENTITY_LABELS,
    FilterKey.CREATED_AT,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.HIDDEN,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.OWNER,
    FilterKey.OWNER_OR_COLLABORATOR,
    FilterKey.REVIEW_LIFECYCLE,
    FilterKey.REVIEW_LIFECYCLE_TYPE,
    FilterKey.TOP_LEVEL_PERMISSIONS,
  ],
  [ModelTypeConstants.ENTRY]: [
    ...ELN_FILTER_KEYS,
    ...ENTRY_FILTER_KEYS,
    ...REVIEWABLE_FILTER_KEYS,
    FilterKey.CUSTOM_TAG,
    FilterKey.ENTRY_TEMPLATE,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.IS_FILL_IN_ENTRY,
  ],
  [ModelTypeConstants.SEQUENCE_ANALYSIS]: [
    FilterKey.CREATED_AT,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.ARCHIVE_PURPOSES,
  ],
  [ModelTypeConstants.STAGE_ENTRY]: [...ELN_FILTER_KEYS, ...ENTRY_FILTER_KEYS, FilterKey.REVIEW],
  [ModelTypeConstants.PROTEIN_ALIGNMENT]: [
    FilterKey.CREATED_AT,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.ARCHIVE_PURPOSES,
  ],
  [ModelTypeConstants.PROCESS]: [
    ...PROCESS_FILTER_KEYS,
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.ARCHIVE_STATUS,
    FilterKey.PROCESS_CLIENT_ORIGIN,
    FilterKey.PROCESS_EXECUTION_TYPE,
    FilterKey.PROCESS_WATCHER,
    FilterKey.ROOT_PROCESS,
    FilterKey.ROOT_PROCESS_CREATOR,
    FilterKey.ROOT_PROCESS_TASK_SCHEMA,
    FilterKey.ROOT_PROCESS_WATCHER,
    FilterKey.SCHEMA,
    FilterKey.REQUEST_V2_SUBMISSION,
  ],
  [ModelTypeConstants.PROCESS_TASK]: compact([
    FilterKey.SCHEMA,
    FilterKey.SCHEMA_FIELD,
    FilterKey.ANY_LINK_SCHEMA_FIELD,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.PROCESS_TASK_ASSIGNEE,
    FilterKey.PROCESS_TASK_STATUS,
    FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES,
    FilterKey.PROCESS_TASK_RESPONSIBLE_PARTIES,
    FilterKey.PROCESS,
    FilterKey.PROCESS_WATCHER,
    FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE,
    FilterKey.PROCESS_TASK_EXECUTION_ORIGIN_ENTRY,
    FilterKey.PROCESS_TASK_HAS_FAILED_MAPPINGS,
    FilterKey.PROCESS_EXECUTION_TYPE,
    FilterKey.ROOT_PROCESS,
    FilterKey.ROOT_PROCESS_TASK_SCHEMA,
    FilterKey.ROOT_PROCESS_CREATOR,
    FilterKey.ROOT_PROCESS_WATCHER,
    FilterKey.ARCHIVE_STATUS,
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.IN_STUDY,
    FilterKey.PROCESS_CLIENT_ORIGIN,
    FilterKey.REQUEST_V2_SUBMISSION,
  ]),
  [ModelTypeConstants.PROCESS_OUTPUT]: [
    FilterKey.SCHEMA,
    FilterKey.SCHEMA_FIELD,
    FilterKey.ANY_LINK_SCHEMA_FIELD,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.PROCESS,
    FilterKey.PROCESS_TASK,
    FilterKey.PROCESS_WATCHER,
    FilterKey.ROOT_PROCESS,
    FilterKey.ROOT_PROCESS_TASK_SCHEMA,
    FilterKey.ROOT_PROCESS_CREATOR,
    FilterKey.ROOT_PROCESS_WATCHER,
    FilterKey.CREATION_ORIGIN_ENTRY,
    FilterKey.ARCHIVE_STATUS,
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.PROCESS_CLIENT_ORIGIN,
    FilterKey.REQUEST_V2_SUBMISSION,
  ],
  [ModelTypeConstants.RECIPE_PROCESS]: PROCESS_FILTER_KEYS,
  [ModelTypeConstants.ASSAY_PROCESS]: PROCESS_FILTER_KEYS,
  [ModelTypeConstants.ASSAY_RUN_TASK]: ALL_ASSAY_TASK_FILTER_KEYS,
  [ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK]: [
    ...ALL_ASSAY_TASK_FILTER_KEYS,
    FilterKey.ASSAY_TEMPLATE,
    FilterKey.PROCESS_TASK_ASSIGNEE,
    FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE,
    FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS,
    FilterKey.ASSAY_RUN_CONDITION_NAMES,
    FilterKey.ASSAY_RUN_REPLICATE_NAMES,
  ],
  [ModelTypeConstants.ASSAY_STEP_TASK]: [
    ...ALL_ASSAY_TASK_FILTER_KEYS,
    FilterKey.PROCESS_TASK_ASSIGNEE,
    FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE,
    FilterKey.ASSAY_TEMPLATE,
    FilterKey.ASSAY_CONDITION_REPLICATE_RUN_TASKS,
    FilterKey.ASSAY_RUN_CONDITION_NAMES,
    FilterKey.ASSAY_METHOD_NAME,
    FilterKey.ASSAY_RUN_REPLICATE_NAMES,
  ],
  [ModelTypeConstants.RECIPE_RUN_TASK]: ALL_RECIPE_TASK_FILTER_KEYS,
  [ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK]: compact([
    ...ALL_RECIPE_TASK_FILTER_KEYS,
    FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS,
    FilterKey.PROCESS_TASK_ASSIGNEE,
    FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE,
    FilterKey.RECIPE_RUN_CONDITION_NAMES,
    FilterKey.RECIPE_RUN_REPLICATE_NAMES,
  ]),
  [ModelTypeConstants.RECIPE_STEP_TASK]: compact([
    ...ALL_RECIPE_TASK_FILTER_KEYS,
    FilterKey.RECIPE_CONDITION_REPLICATE_RUN_TASKS,
    FilterKey.PROCESS_TASK_ASSIGNEE,
    FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE,
    FilterKey.RECIPE_METHOD_NAME,
    FilterKey.RECIPE_RUN_CONDITION_NAMES,
    FilterKey.RECIPE_RUN_REPLICATE_NAMES,
  ]),
  [ModelTypeConstants.PROTOCOL]: [
    ...ELN_FILTER_KEYS,
    FilterKey.CREATOR,
    FilterKey.CUSTOM_TAG,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
  ],
  [ModelTypeConstants.WORKFLOW]: [
    ...ELN_FILTER_KEYS,
    FilterKey.WORKFLOW_TEMPLATE,
    FilterKey.WORKFLOW_STATUS,
  ].filter((key, index) => key !== FilterKey.NOTE_REFERENCE),
  [ModelTypeConstants.CONTAINER]: compact([
    ...STORABLE_FILTER_KEYS,
    FilterKey.CONTAINER_CONCENTRATION,
    FilterKey.CONTAINER_QUANTITY,
    FilterKey.CONTAINER_CHECK_OUT_ASSIGNEE,
    FilterKey.CONTAINER_CHECK_OUT_STATUS,
    BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS ? FilterKey.ITEM_IN_WORKLIST : null,
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) ? FilterKey.SAMPLE_AUTHORIZED_USERS : null,
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) ? FilterKey.SAMPLE_OWNERS : null,
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) ? FilterKey.SAMPLE_RESTRICTION_STATUS : null,
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) ? FilterKey.SAMPLE_RESTRICTION_ACCESS : null,
    BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE ? FilterKey.EXPIRATION_DATE : null,
    BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE ? FilterKey.IS_EXPIRED : null,
    BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW ? FilterKey.FREEZE_THAW_COUNT : null,
  ]),
  [ModelTypeConstants.PLATE]: compact([
    ...STORABLE_FILTER_KEYS,
    BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS ? FilterKey.ITEM_IN_WORKLIST : null,
    FilterKey.GRID_EMPTY_CONTAINERS,
    FilterKey.GRID_EMPTY_POSITIONS,
  ]),
  [ModelTypeConstants.BOX]: [
    ...STORABLE_FILTER_KEYS,
    FilterKey.GRID_EMPTY_CONTAINERS,
    FilterKey.GRID_EMPTY_POSITIONS,
  ],
  [ModelTypeConstants.LOCATION]: compact([
    ...STORABLE_FILTER_KEYS,
    FilterKey.INVENTORY_VALIDATION_STATUS,
    FilterKey.INVENTORY_VALIDATION_ERROR_LABEL,
  ]),
  [ModelTypeConstants.WORKLIST]: [
    FilterKey.NOTE_REFERENCE,
    FilterKey.CREATED_AT,
    FilterKey.MODIFIED_AT,
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.CREATOR,
    FilterKey.WORKLIST_TYPE,
    FilterKey.OWNER,
  ],
  // This was originally misconfigured as PROJECT even though the TYPES filter uses SOURCE as the type,
  // but it was left in case some caller is depending on it. These are essentially aliases for each other.
  [ModelTypeConstants.SOURCE]: PROJECT_FILTER_KEYS,
  [ModelTypeConstants.PROJECT]: PROJECT_FILTER_KEYS,
  [ModelTypeConstants.INSTITUTION]: [FilterKey.INSTITUTION],
  [ModelTypeConstants.ORGANIZATION]: [FilterKey.ORGANIZATION],
  [ModelTypeConstants.TEAM]: [FilterKey.TEAM],
  [ModelTypeConstants.USER]: [FilterKey.ACCOUNT_STATUS],
  [ModelTypeConstants.CONNECTION]: [
    FilterKey.SCHEMA,
    FilterKey.SCHEMA_FIELD,
    FilterKey.ANY_LINK_SCHEMA_FIELD,
    FilterKey.CONNECTION_GATEWAY,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.ARCHIVE_STATUS,
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.NOTE_REFERENCE,
    FilterKey.OWNER,
  ],
  [ModelTypeConstants.JELLYFISH]: [
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.CREATED_AT,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
  ],
  [ModelTypeConstants.DATASET]: [
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
  ],
  [ModelTypeConstants.FILE]: [
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.CREATED_AT,
    FilterKey.CREATOR,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
  ],
  [ModelTypeConstants.WORKSHEET]: [
    ...REVIEWABLE_FILTER_KEYS,
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.CREATED_AT,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
  ],
  [ModelTypeConstants.BENCHLING_APP]: [
    FilterKey.MODIFIED_AT,
    FilterKey.SESSION_MODIFIED_AT,
    FilterKey.SESSION_NAME,
    FilterKey.SESSION_STATUS,
  ],
  [ModelTypeConstants.STUDY]: [
    FilterKey.ANCESTOR_FOLDER,
    FilterKey.CREATED_AT,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.SCHEMA,
    FilterKey.AUTHOR,
    FilterKey.FOLDER,
    FilterKey.FOLDER_SECTION,
    FilterKey.STUDY_PHASE,
    FilterKey.SCHEMA_FIELD,
  ],
  [ModelTypeConstants.TEMPLATE]: BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING
    ? PUBLISHABLE_FILTER_KEYS
    : COLLECTIBLE_FILTER_KEYS,
  [ModelTypeConstants.SUBTEMPLATE]: BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING
    ? PUBLISHABLE_FILTER_KEYS
    : COLLECTIBLE_FILTER_KEYS,
  [ModelTypeConstants.FORM_DEFINITION]: COLLECTIBLE_FILTER_KEYS,
  [ModelTypeConstants.RECIPE_PROCEDURE]:
    BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING && BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING
      ? PUBLISHABLE_FILTER_KEYS
      : COLLECTIBLE_FILTER_KEYS,
  [ModelTypeConstants.ASSAY_PROCEDURE]:
    BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING && BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING
      ? PUBLISHABLE_FILTER_KEYS
      : COLLECTIBLE_FILTER_KEYS,
  [ModelTypeConstants.REQUEST_V2_DEFINITION]: COLLECTIBLE_FILTER_KEYS,
  [ModelTypeConstants.TEMPLATE_COLLECTION]: [
    FilterKey.ARCHIVE_PURPOSES,
    FilterKey.OWNER,
    FilterKey.CREATED_AT,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
  ],
  [ModelTypeConstants.TEST_DEFINITION]: [FilterKey.NAMES],
  [ModelTypeConstants.TEST_ORDER]: [
    FilterKey.ASSAY_TEMPLATE,
    FilterKey.CREATED_AT,
    FilterKey.DILUTION_FACTOR,
    FilterKey.MODIFIED_AT,
    FilterKey.NAMES,
    FilterKey.SAMPLE,
    FilterKey.SAMPLE_CONTAINER,
    FilterKey.SAMPLE_DEFINITION,
    FilterKey.TEST_STATUS,
    FilterKey.WORKSHEET,
  ],
  [ModelTypeConstants.PREDICTION_BATCH]: [],
};

const _QUICK_FILTERS_BY_TYPE: Record<string, Array<QuickFilterKey>> = {
  [ModelTypeConstants.PROCESS_TASK]: [QuickFilterKey.TASKS_ASSIGNED_TO_CURRENT_USER],
  [ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK]: [QuickFilterKey.TASKS_ASSIGNED_TO_CURRENT_USER],
  [ModelTypeConstants.RECIPE_STEP_TASK]: [QuickFilterKey.TASKS_ASSIGNED_TO_CURRENT_USER],
  [ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK]: [QuickFilterKey.TASKS_ASSIGNED_TO_CURRENT_USER],
  [ModelTypeConstants.ASSAY_STEP_TASK]: [QuickFilterKey.TASKS_ASSIGNED_TO_CURRENT_USER],
};

/**
 * Define which filters should be automatically supported for Core Objects implementing
 * a given PlatformCapability. This mapping is currently incomplete, as the filters for
 * e.g. Archivable are defined manually above rather than here. As we add items to
 * CAPABILITY_INTEGRATION_MAP, we'll be able to update this and remove those keys from
 * FILTER_KEYS_BY_TYPE.
 */
const FILTER_KEYS_BY_CAPABILITY: Partial<Record<InterfaceType, Array<FilterKey>>> = {
  [DomainGraphInterface.CORE_OBJECT_ARCHIVABLE]: [FilterKey.ARCHIVE_PURPOSES],
  [DomainGraphInterface.DATA_CATALOG_ITEM]: [FilterKey.LINEAGE_PARENT_API_IDENTIFIER],
  [DomainGraphInterface.HAS_CUSTOM_FIELDS]: [FilterKey.CUSTOM_TAG],
  [DomainGraphInterface.STARRABLE]: [FilterKey.STARRED],
  [DomainGraphInterface.STUDYABLE]: [FilterKey.IN_STUDY],
};

function getFilterKeysForModelTypeAndCapability(
  modelType: string,
  interfaceType: InterfaceType
): Array<FilterKey> {
  const objectType = getObjectTypeForModelTypeConstant(modelType as ModelTypeConstants);
  // TODO(BNCH-79067): remove studyable special casing
  if (
    interfaceType !== DomainGraphInterface.STUDYABLE ||
    (interfaceType === DomainGraphInterface.STUDYABLE && objectType && isStudyableObjectType(objectType))
  ) {
    return FILTER_KEYS_BY_CAPABILITY[interfaceType] ?? [];
  } else {
    return [];
  }
}

function getPluginFilterKeysForModelType(modelType: ModelTypeConstants): Array<FilterKey> {
  const filterKeys = [];
  for (const plugin of registeredSearchPlugins.getRegisteredSearchPlugins()) {
    if (plugin.pluginApplies(modelType)) {
      filterKeys.push(...plugin.supportedFilters);
    }
  }
  return filterKeys;
}

export function getQuickFilterKeysForModelType(modelType: string): Array<QuickFilterKey> {
  return _QUICK_FILTERS_BY_TYPE[modelType] ?? [];
}

// Keep in sync with SightglassType.
export function getFilterKeysForModelType(modelType: string): Array<FilterKey> {
  const manuallyDefinedFilters = _FILTER_KEYS_BY_TYPE[modelType] || [];

  let coreObjectFilters: Array<FilterKey> = [];
  const capabilities = getCapabilitiesForModelType(modelType as ModelTypeConstants);
  for (const capability of capabilities) {
    coreObjectFilters = coreObjectFilters.concat(
      getFilterKeysForModelTypeAndCapability(modelType, capability)
    );
  }

  return [
    // Wrap in a set to de-deplicate filter keys
    ...new Set<FilterKey>([
      ...manuallyDefinedFilters,
      ...coreObjectFilters,
      ...getPluginFilterKeysForModelType(modelType as ModelTypeConstants),
      FilterKey.API_IDS,
    ]),
  ];
}

export function isFilterValidForTypes(filterKey: FilterKey, types: Array<string>): boolean {
  if (filterKey === FilterKey.API_IDS) {
    return true;
  }

  const typesSupportedByManuallyDefinedFilters = types.some((type: string) =>
    (_FILTER_KEYS_BY_TYPE[type] || []).includes(filterKey)
  );
  const typesSupportedByCapabilityDefinedFilters = types.some(
    (modelType: ModelTypeConstants & SequenceSubtype) => {
      const capabilities = getCapabilitiesForModelType(modelType);
      return capabilities.some((capability) =>
        getFilterKeysForModelTypeAndCapability(modelType, capability).includes(filterKey)
      );
    }
  );
  const typesSupportedByPluginDefinedFilters = types.some((modelType: ModelTypeConstants) => {
    const pluginFilters = getPluginFilterKeysForModelType(modelType);
    return pluginFilters.includes(filterKey);
  });

  return (
    typesSupportedByManuallyDefinedFilters ||
    typesSupportedByCapabilityDefinedFilters ||
    typesSupportedByPluginDefinedFilters
  );
}
